import type { Location, Reference } from '@medplum/fhirtypes'
import { AdmissionEncounter } from '@seen/core/models/encounters/admission-encounter'
import { ExtendedLocation } from '@seen/core/models/location'
import { type JSX, type ReactNode, useMemo } from 'react'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { ExtendedResourceTable } from '#src/components/fhir-table/ExtendedResourceTable.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { useRead } from '#src/hooks/useRead.ts'

interface AdmissionEncounterWithLocation extends AdmissionEncounter {
  locationName?: string
}

export function AdmissionTable({
  patientId,
  pagePrefix,
}: {
  patientId?: string
  pagePrefix: string
}): JSX.Element {
  const navigate = useNavigateFromClickEvent()

  const columnConfigs = useMemo<ColumnConfig<AdmissionEncounterWithLocation>[]>(
    () => [
      {
        id: 'start',
        headerText: 'Start Date',
        render: (encounter) => <DateDisplay value={encounter.startTime} />,
        valueType: 'date' as const,
        getSortFilterValue: (encounter) =>
          encounter.startTime ? new Date(encounter.startTime) : undefined,
        filteringType: 'date-range',
        enableSorting: true,
        useAsInitialSort: true,
        sortDescFirst: true,
      },
      {
        id: 'end',
        headerText: 'End Date',
        render: (encounter) => <DateDisplay value={encounter.endTime} />,
        valueType: 'date' as const,
        getSortFilterValue: (encounter) =>
          encounter.endTime ? new Date(encounter.endTime) : undefined,
        filteringType: 'date-range',
        enableSorting: true,
      },
      {
        id: 'location',
        headerText: 'Location',
        render: (encounter) => (
          <LocationDisplay locationRef={encounter.location} />
        ),
        valueType: 'text' as const,
        getSortFilterValue: (encounter) => encounter.locationName,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'status',
        headerText: 'Status',
        render: (encounter) => <>{encounter.status}</>,
        valueType: 'text' as const,
        getSortFilterValue: (encounter) => encounter.status,
        filteringType: 'text',
        enableSorting: true,
      },
    ],
    []
  )

  const filterQuery = useMemo(() => {
    const baseQuery = {
      patient: patientId,
      class: AdmissionEncounter.ENCOUNTER_CLASS.HOSPITAL_INPATIENT,
    }
    return baseQuery
  }, [patientId])

  return (
    <ExtendedResourceTable
      resourceClass={AdmissionEncounter}
      filterQuery={filterQuery}
      columnConfigs={columnConfigs}
      tableOptions={{
        mantineTableBodyRowProps: ({ row }) => ({
          onClick: (event) => {
            const urlPath = `${pagePrefix}/${row.original.id}`
            navigate(urlPath, event)
          },
          className: 'cursor-pointer',
        }),
      }}
    />
  )
}

function LocationDisplay({
  locationRef,
}: {
  locationRef?: Reference<Location>
}): ReactNode {
  const { data: location } = useRead(ExtendedLocation, locationRef?.reference)

  if (!location) {
    return 'Unknown'
  }

  return location.name
}
